import React, { useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { ChevronDown, Gamepad2, Smartphone } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { LogoIcon } from "./LogoIcon";

function FloatingPixels() {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    let animId;
    let particles = [];

    const resize = () => {
      canvas.width = canvas.offsetWidth;
      canvas.height = canvas.offsetHeight;
    };
    resize();
    window.addEventListener("resize", resize);

    const colors = ["#F5921B", "#00D4D4", "#F5921B80", "#00D4D480"];

    for (let i = 0; i < 50; i++) {
      particles.push({
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height,
        size: Math.random() * 4 + 1,
        speedX: (Math.random() - 0.5) * 0.4,
        speedY: (Math.random() - 0.5) * 0.4,
        color: colors[Math.floor(Math.random() * colors.length)],
        opacity: Math.random() * 0.5 + 0.1,
      });
    }

    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      particles.forEach((p) => {
        ctx.globalAlpha = p.opacity;
        ctx.fillStyle = p.color;
        ctx.fillRect(p.x, p.y, p.size, p.size);
        p.x += p.speedX;
        p.y += p.speedY;
        if (p.x < 0) p.x = canvas.width;
        if (p.x > canvas.width) p.x = 0;
        if (p.y < 0) p.y = canvas.height;
        if (p.y > canvas.height) p.y = 0;
      });
      ctx.globalAlpha = 1;
      animId = requestAnimationFrame(animate);
    };
    animate();

    return () => {
      cancelAnimationFrame(animId);
      window.removeEventListener("resize", resize);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className="absolute inset-0 w-full h-full pointer-events-none"
      style={{ opacity: 0.6 }}
    />
  );
}

function GridBackground() {
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      <div
        className="absolute inset-0"
        style={{
          backgroundImage: `
            linear-gradient(rgba(245,146,27,0.03) 1px, transparent 1px),
            linear-gradient(90deg, rgba(245,146,27,0.03) 1px, transparent 1px)
          `,
          backgroundSize: "60px 60px",
        }}
      />
      <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-brand-orange/5 rounded-full blur-[120px]" />
      <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-brand-cyan/5 rounded-full blur-[120px]" />
    </div>
  );
}

export default function HeroSection() {
  const { t } = useLang();

  const scrollTo = (href) => {
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      data-testid="hero-section"
      className="relative min-h-screen flex items-center justify-center overflow-hidden"
    >
      <GridBackground />
      <FloatingPixels />

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32 w-full">
        <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-20">
          {/* Text */}
          <div className="flex-1 text-center lg:text-left">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.2 }}
              className="inline-flex items-center gap-2 px-4 py-1.5 mb-6 border border-white/10 rounded-full text-xs font-body text-white/50 uppercase tracking-wider"
            >
              <Gamepad2 size={14} className="text-brand-orange" />
              <span>Android</span>
              <span className="text-white/20">&</span>
              <span>iOS</span>
              <Smartphone size={14} className="text-brand-cyan" />
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.3 }}
              className="font-display font-bold text-4xl sm:text-5xl lg:text-7xl leading-[0.95] mb-6"
            >
              <span className="text-white">{t.hero.title1}</span>
              <br />
              <span className="bg-gradient-to-r from-brand-orange to-brand-cyan bg-clip-text text-transparent">
                {t.hero.title2}
              </span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.5 }}
              className="font-body text-white/50 text-base lg:text-lg max-w-lg mx-auto lg:mx-0 mb-8 leading-relaxed"
            >
              {t.hero.subtitle}
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.7 }}
              className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start"
            >
              <button
                data-testid="hero-cta-primary"
                onClick={() => scrollTo("#about")}
                className="px-7 py-3.5 border border-brand-cyan/40 text-brand-cyan font-bold uppercase text-sm tracking-wider hover:bg-brand-cyan/10 transition-all duration-300"
              >
                {t.hero.cta1}
              </button>
            </motion.div>
          </div>

          {/* Floating Logo */}
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1, delay: 0.4 }}
            className="flex-shrink-0 relative"
          >
            <div className="relative">
              <motion.div
                animate={{ y: [-8, 8, -8] }}
                transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
              >
                <LogoIcon size={240} className="sm:w-[280px] sm:h-[280px] lg:w-[340px] lg:h-[340px]" />
              </motion.div>
              {/* Glow behind logo */}
              <div className="absolute inset-0 -z-10 blur-[80px] opacity-30">
                <div className="absolute top-1/4 left-0 w-1/2 h-1/2 bg-brand-cyan rounded-full" />
                <div className="absolute bottom-1/4 right-0 w-1/2 h-1/2 bg-brand-orange rounded-full" />
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Scroll indicator */}
      <motion.button
        data-testid="scroll-indicator"
        onClick={() => scrollTo("#about")}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.5 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 text-white/30 hover:text-white/60 transition-colors"
      >
        <motion.div animate={{ y: [0, 8, 0] }} transition={{ duration: 2, repeat: Infinity }}>
          <ChevronDown size={28} />
        </motion.div>
      </motion.button>
    </section>
  );
}
